import React, { useEffect } from 'react';

import {
    AhvOppfolgingDTO,
    FinansieringsbevisResponse,
    KundeSigneringsordreDTO,
    Samtykkeoppdrag,
} from '../../generated-types';
import { useSendAntiMoneyLaunderingNotification } from '../../hooks/useAntiMoneyLaunderingAssignment';
import { useTranslation } from '../../translations';
import { findActiveTasks, findArchivedTasks, sortByDeadline, sortByLastStatus } from '../../utils/tasksUtils';
import { AntiMoneyLaunderingAssignment } from '../AntiMoneyLaunderingAssignment';
import { ConsentAssignment } from '../ConsentAssignment';
import { FundingCertificate } from '../FundingCertificate';
import { SigningOrder } from '../SigningOrder';
import { TaskSkeleton } from '../TaskSkeleton';

import './TasksList.scss';

interface TasksListInterface {
    signingOrders: KundeSigneringsordreDTO[];
    fundingCertificates: FinansieringsbevisResponse[];
    antiMoneyLaunderingAssignments: AhvOppfolgingDTO[];
    consentAssignments: Samtykkeoppdrag[];
    currentUserFnr: string;
    isLoading: boolean;
}

const TasksList = ({
    signingOrders,
    antiMoneyLaunderingAssignments,
    isLoading,
    currentUserFnr,
    fundingCertificates,
    consentAssignments,
}: TasksListInterface) => {
    const { documentsHeader, recentlyReviewedHeader, noDocumentsForReview, noNewlyReviewedTasks } = useTranslation();

    const activeTasks = signingOrders.filter(findActiveTasks).sort(sortByDeadline);
    const lastArchivedSigningOrders = signingOrders.filter(findArchivedTasks).sort(sortByLastStatus).slice(0, 3);
    const hasSigningOrders = activeTasks?.length > 0;
    const hasAntiMoneyLaunderingAssignment = antiMoneyLaunderingAssignments?.length > 0;
    const hasFundingCertificates = fundingCertificates?.length > 0;

    const urlHash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.href);
    const customerId = searchParams.get('customerId');

    const ahvOk = urlHash.includes('ahv=ok');
    const ahvError = urlHash.includes('ahv=error');
    const { mutate: sendNotification } = useSendAntiMoneyLaunderingNotification();
    const completedConsentAssignmentStatuses = ['ACCEPTED', 'REJECTED', 'EXPIRED'];
    const completedConsentAssignments = consentAssignments.filter((consentAssignment) =>
        completedConsentAssignmentStatuses.includes(consentAssignment.status),
    );
    const activeConsentAssignments = consentAssignments.filter(
        (consentAssignment) => !completedConsentAssignmentStatuses.includes(consentAssignment.status),
    );
    const hasConsentAssignments = activeConsentAssignments?.length > 0;
    const hasTasks =
        hasSigningOrders || hasFundingCertificates || hasAntiMoneyLaunderingAssignment || hasConsentAssignments;
    const hasArchivedTasks = completedConsentAssignments.length > 0 || lastArchivedSigningOrders.length > 0;

    useEffect(() => {
        if (ahvOk && customerId) sendNotification(customerId);
    }, [sendNotification, ahvOk, customerId]);

    return (
        <div className="tasks-list-wrapper">
            <div>
                <h2>{documentsHeader}</h2>
                {isLoading ? (
                    <TaskSkeleton />
                ) : (
                    <div className="tasks-list-wrapper__list">
                        {hasFundingCertificates && <FundingCertificate fundingCertificates={fundingCertificates} />}
                        {(hasAntiMoneyLaunderingAssignment || ahvOk || ahvError) && (
                            <AntiMoneyLaunderingAssignment
                                antiMoneyLaunderingAssignments={antiMoneyLaunderingAssignments}
                                ahvOk={ahvOk}
                                ahvError={ahvError}
                            />
                        )}
                        {hasConsentAssignments &&
                            activeConsentAssignments.map((consentAssignment) => (
                                <ConsentAssignment
                                    key={consentAssignment.sakUuid}
                                    consentAssignmentTask={consentAssignment}
                                />
                            ))}
                        {hasSigningOrders &&
                            currentUserFnr &&
                            activeTasks.map((signingOrder) => (
                                <SigningOrder
                                    signingOrder={signingOrder}
                                    key={signingOrder.id}
                                    currentUserFnr={currentUserFnr}
                                />
                            ))}
                        {!hasTasks && <span>{noDocumentsForReview}</span>}
                    </div>
                )}
            </div>
            <div>
                <h2>{recentlyReviewedHeader}</h2>
                {isLoading ? (
                    <TaskSkeleton />
                ) : (
                    <div className="tasks-list-wrapper__list">
                        {hasArchivedTasks ? (
                            <>
                                {lastArchivedSigningOrders.length > 0 &&
                                    currentUserFnr &&
                                    lastArchivedSigningOrders.map((signingOrder) => (
                                        <SigningOrder
                                            signingOrder={signingOrder}
                                            key={signingOrder.id}
                                            currentUserFnr={currentUserFnr}
                                        />
                                    ))}
                                {completedConsentAssignments.length > 0 &&
                                    completedConsentAssignments.map((completedConsentAssignment) => (
                                        <ConsentAssignment
                                            key={completedConsentAssignment.sakUuid}
                                            consentAssignmentTask={completedConsentAssignment}
                                        />
                                    ))}
                            </>
                        ) : (
                            <span>{noNewlyReviewedTasks}</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TasksList;
